import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { createClient } from "@supabase/supabase-js";

const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL,
  process.env.REACT_APP_SUPABASE_ANON_KEY
);

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=JetBrains+Mono:wght@400;500&display=swap');
  * { box-sizing: border-box; margin: 0; padding: 0; }
  :root {
    --bg: #0a0e1a; --surface: #111827; --surface2: #1a2235;
    --border: rgba(255,255,255,0.07); --accent: #00d4ff; --accent2: #7b5ea7;
    --success: #22c55e; --error: #ef4444; --warning: #f59e0b;
    --text: #e8edf5; --muted: #6b7a99;
    --font-display: 'Syne', sans-serif; --font-mono: 'JetBrains Mono', monospace;
  }
  body { background: var(--bg); color: var(--text); font-family: var(--font-display); min-height: 100vh; overflow-x: hidden; }
  .app { min-height: 100vh; background: radial-gradient(ellipse at 20% 50%, rgba(0,212,255,0.04) 0%, transparent 60%), radial-gradient(ellipse at 80% 20%, rgba(123,94,167,0.06) 0%, transparent 50%), var(--bg); }
  .header { padding: 20px 32px; border-bottom: 1px solid var(--border); display: flex; align-items: center; justify-content: space-between; background: rgba(17,24,39,0.8); backdrop-filter: blur(12px); position: sticky; top: 0; z-index: 100; }
  .logo { display: flex; align-items: center; gap: 12px; cursor: pointer; }
  .logo:hover { opacity: 0.8; }
  .logo-emoji { font-size: 28px; line-height: 1; }
  .logo-text { font-size: 16px; font-weight: 800; color: var(--text); }
  .logo-sub { font-size: 11px; color: var(--muted); font-weight: 400; font-family: var(--font-mono); }
  .header-stats { display: flex; gap: 16px; align-items: center; }
  .stat-pill { display: flex; align-items: center; gap: 8px; background: var(--surface); border: 1px solid var(--border); padding: 6px 14px; border-radius: 100px; font-size: 13px; font-family: var(--font-mono); }
  .stat-dot { width: 7px; height: 7px; border-radius: 50%; }
  .progress-bar-container { background: var(--border); height: 3px; }
  .progress-bar-fill { height: 100%; background: linear-gradient(90deg, var(--accent), var(--accent2)); transition: width 0.5s ease; }
  .main { max-width: 900px; margin: 0 auto; padding: 40px 24px; }
  .loading-screen { display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 60vh; gap: 20px; }
  .loading-spinner { width: 48px; height: 48px; border: 3px solid var(--border); border-top-color: var(--accent); border-radius: 50%; animation: spin 0.8s linear infinite; }
  .loading-text { font-family: var(--font-mono); font-size: 13px; color: var(--muted); letter-spacing: 0.1em; }
  .error-screen { display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 60vh; gap: 16px; text-align: center; }
  .error-title { font-size: 20px; font-weight: 700; color: var(--error); }
  .error-msg { font-size: 14px; color: var(--muted); max-width: 400px; line-height: 1.6; }
  .start-screen { text-align: center; padding: 80px 24px; animation: fadeUp 0.6s ease; }
  .start-eyebrow { font-family: var(--font-mono); font-size: 12px; letter-spacing: 0.2em; color: var(--accent); text-transform: uppercase; margin-bottom: 20px; }
  .start-title { font-size: clamp(36px, 6vw, 64px); font-weight: 800; line-height: 1.1; margin-bottom: 16px; background: linear-gradient(135deg, #ffffff 0%, rgba(255,255,255,0.6) 100%); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; }
  .start-sub { font-size: 16px; color: var(--muted); margin-bottom: 48px; line-height: 1.6; max-width: 520px; margin-left: auto; margin-right: auto; }
  .start-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; margin-bottom: 48px; max-width: 600px; margin-left: auto; margin-right: auto; }
  .start-card { background: var(--surface); border: 1px solid var(--border); padding: 20px; border-radius: 12px; text-align: center; }
  .start-card-num { font-size: 28px; font-weight: 800; color: var(--accent); font-family: var(--font-mono); display: block; margin-bottom: 4px; }
  .start-card-label { font-size: 12px; color: var(--muted); font-family: var(--font-mono); letter-spacing: 0.05em; }
  .btn-primary { background: linear-gradient(135deg, var(--accent), var(--accent2)); color: white; border: none; padding: 14px 36px; font-size: 15px; font-weight: 700; font-family: var(--font-display); border-radius: 10px; cursor: pointer; transition: all 0.2s; }
  .btn-primary:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(0,212,255,0.25); }
  .question-wrapper { animation: fadeUp 0.4s ease; }
  .question-meta { display: flex; align-items: center; justify-content: space-between; margin-bottom: 20px; }
  .q-number { font-family: var(--font-mono); font-size: 12px; color: var(--muted); letter-spacing: 0.1em; }
  .q-topic { font-family: var(--font-mono); font-size: 11px; color: var(--accent); background: rgba(0,212,255,0.08); border: 1px solid rgba(0,212,255,0.15); padding: 4px 10px; border-radius: 100px; max-width: 300px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .question-card { background: var(--surface); border: 1px solid var(--border); border-radius: 16px; padding: 32px; margin-bottom: 24px; }
  .multi-badge { display: inline-flex; align-items: center; gap: 6px; font-family: var(--font-mono); font-size: 11px; color: var(--warning); background: rgba(245,158,11,0.08); border: 1px solid rgba(245,158,11,0.2); padding: 4px 10px; border-radius: 100px; margin-bottom: 16px; letter-spacing: 0.05em; }
  .question-text { font-size: 17px; line-height: 1.6; margin-bottom: 28px; font-weight: 600; }
  .code-block { background: var(--surface2); border: 1px solid var(--border); border-radius: 8px; padding: 16px; font-family: var(--font-mono); font-size: 13px; overflow-x: auto; margin: 12px 0; white-space: pre-wrap; }
  .options-grid { display: flex; flex-direction: column; gap: 10px; }
  .option-btn { display: flex; align-items: flex-start; gap: 14px; background: var(--surface2); border: 1px solid var(--border); border-radius: 10px; padding: 14px 16px; cursor: pointer; transition: all 0.15s; text-align: left; width: 100%; }
  .option-btn:hover:not(:disabled) { border-color: rgba(0,212,255,0.3); background: rgba(0,212,255,0.04); }
  .option-btn:disabled { cursor: default; }
  .option-btn.selected { border-color: var(--accent); background: rgba(0,212,255,0.08); }
  .option-btn.correct-opt { border-color: var(--success); background: rgba(34,197,94,0.08); }
  .option-btn.wrong-opt { border-color: var(--error); background: rgba(239,68,68,0.08); }
  .option-letter { font-family: var(--font-mono); font-size: 12px; font-weight: 600; color: var(--muted); background: var(--surface); border: 1px solid var(--border); width: 24px; height: 24px; border-radius: 6px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; margin-top: 1px; }
  .option-text { font-size: 14px; line-height: 1.5; color: var(--text); }
  .feedback-box { margin-top: 24px; padding: 16px 20px; border-radius: 10px; }
  .correct-fb { background: rgba(34,197,94,0.06); border: 1px solid rgba(34,197,94,0.2); }
  .incorrect-fb { background: rgba(239,68,68,0.06); border: 1px solid rgba(239,68,68,0.2); }
  .feedback-header { display: flex; align-items: center; gap: 8px; font-weight: 700; font-size: 14px; margin-bottom: 8px; }
  .feedback-text { font-size: 13px; color: rgba(232,237,245,0.8); line-height: 1.6; }
  .actions { display: flex; justify-content: flex-end; gap: 12px; }
  .btn-submit { background: linear-gradient(135deg, var(--accent), var(--accent2)); color: white; border: none; padding: 13px 32px; font-size: 14px; font-weight: 700; font-family: var(--font-display); border-radius: 10px; cursor: pointer; transition: all 0.2s; }
  .btn-submit:hover:not(:disabled) { transform: translateY(-1px); }
  .btn-submit:disabled { opacity: 0.4; cursor: default; }
  .btn-next { background: var(--surface2); color: var(--text); border: 1px solid var(--border); padding: 13px 32px; font-size: 14px; font-weight: 600; font-family: var(--font-display); border-radius: 10px; cursor: pointer; transition: all 0.2s; }
  .btn-next:hover { border-color: rgba(255,255,255,0.2); background: rgba(255,255,255,0.05); }
  .results-screen { animation: fadeUp 0.6s ease; max-width: 700px; margin: 0 auto; }
  .results-hero { text-align: center; margin-bottom: 48px; }
  .score-ring { width: 160px; height: 160px; margin: 0 auto 24px; position: relative; }
  .score-ring svg { transform: rotate(-90deg); }
  .score-center { position: absolute; inset: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; }
  .score-pct { font-size: 36px; font-weight: 800; font-family: var(--font-mono); color: var(--accent); }
  .score-label { font-size: 11px; color: var(--muted); font-family: var(--font-mono); letter-spacing: 0.1em; }
  .results-title { font-size: 28px; font-weight: 800; margin-bottom: 8px; }
  .results-sub { font-size: 15px; color: var(--muted); }
  .results-breakdown { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; margin-bottom: 40px; }
  .breakdown-card { background: var(--surface); border: 1px solid var(--border); border-radius: 12px; padding: 20px; text-align: center; }
  .breakdown-num { font-size: 32px; font-weight: 800; font-family: var(--font-mono); display: block; margin-bottom: 4px; }
  .breakdown-label { font-size: 12px; color: var(--muted); font-family: var(--font-mono); letter-spacing: 0.05em; }
  .results-actions { display: flex; gap: 12px; justify-content: center; flex-wrap: wrap; }
  .btn-secondary { background: var(--surface); color: var(--text); border: 1px solid var(--border); padding: 13px 28px; font-size: 14px; font-weight: 600; font-family: var(--font-display); border-radius: 10px; cursor: pointer; transition: all 0.2s; }
  .btn-secondary:hover { border-color: rgba(255,255,255,0.2); background: rgba(255,255,255,0.05); }
  .review-section { margin-top: 48px; }
  .review-title { font-size: 13px; font-weight: 600; color: var(--muted); font-family: var(--font-mono); letter-spacing: 0.1em; text-transform: uppercase; margin-bottom: 20px; }
  .review-item { background: var(--surface); border: 1px solid var(--border); border-radius: 12px; padding: 20px 24px; margin-bottom: 12px; border-left: 3px solid var(--error); }
  .review-q { font-size: 14px; line-height: 1.5; margin-bottom: 10px; font-weight: 600; }
  .review-exp { font-size: 13px; color: rgba(232,237,245,0.7); line-height: 1.6; }
  .confirm-modal { position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%); background: var(--surface); border: 1px solid var(--border); border-radius: 16px; padding: 32px; z-index: 300; width: 320px; box-shadow: 0 24px 64px rgba(0,0,0,0.6); animation: fadeUp 0.2s ease; }
  .confirm-title { font-size: 18px; font-weight: 700; margin-bottom: 8px; }
  .confirm-body { font-size: 14px; color: var(--muted); margin-bottom: 24px; line-height: 1.5; }
  .confirm-actions { display: flex; gap: 10px; justify-content: flex-end; }
  .btn-danger { background: var(--error); color: white; border: none; padding: 10px 20px; font-size: 14px; font-weight: 700; font-family: var(--font-display); border-radius: 8px; cursor: pointer; transition: all 0.2s; }
  .btn-danger:hover { background: #f87171; transform: translateY(-1px); }
  .btn-home { background: transparent; color: var(--muted); border: 1px solid var(--border); padding: 6px 14px; font-size: 12px; font-weight: 600; font-family: var(--font-display); border-radius: 100px; cursor: pointer; transition: all 0.2s; }
  .btn-home:hover { color: var(--text); border-color: rgba(255,255,255,0.2); }
  .btn-nav-grid { background: rgba(0,212,255,0.08); color: var(--accent); border: 1px solid rgba(0,212,255,0.2); padding: 6px 14px; font-size: 12px; font-weight: 600; font-family: var(--font-display); border-radius: 100px; cursor: pointer; transition: all 0.2s; display: flex; align-items: center; gap: 6px; }
  .btn-nav-grid:hover { background: rgba(0,212,255,0.12); }
  .nav-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); z-index: 150; }
  .nav-drawer { position: fixed; right: 0; top: 0; bottom: 0; width: 280px; background: var(--surface); border-left: 1px solid var(--border); z-index: 200; padding: 24px; overflow-y: auto; animation: slideIn 0.2s ease; }
  .nav-drawer-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 20px; }
  .nav-drawer-title { font-size: 14px; font-weight: 700; font-family: var(--font-mono); letter-spacing: 0.1em; text-transform: uppercase; color: var(--muted); }
  .nav-close { background: transparent; border: none; color: var(--muted); font-size: 18px; cursor: pointer; padding: 4px; }
  .nav-close:hover { color: var(--text); }
  .nav-legend { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 20px; }
  .legend-item { display: flex; align-items: center; gap: 6px; font-size: 11px; color: var(--muted); font-family: var(--font-mono); }
  .legend-dot { width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; }
  .nav-grid { display: grid; grid-template-columns: repeat(5, 1fr); gap: 8px; }
  .nav-q-btn { background: var(--surface2); border: 1px solid var(--border); border-radius: 8px; padding: 10px; font-size: 13px; font-family: var(--font-mono); font-weight: 600; width: 100%; min-width: 0; color: var(--muted); cursor: pointer; transition: all 0.15s; }
  .nav-q-btn:hover { border-color: rgba(255,255,255,0.2); color: var(--text); }
  @keyframes spin { to { transform: rotate(360deg); } }
  @keyframes fadeUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
  @keyframes slideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }
  @media (max-width: 600px) {
    .header { padding: 16px 20px; }
    .header-stats { gap: 8px; }
    .stat-pill { display: none; }
    .main { padding: 24px 16px; }
    .question-card { padding: 20px; }
    .start-grid { grid-template-columns: repeat(3, 1fr); gap: 10px; }
    .results-breakdown { grid-template-columns: repeat(3, 1fr); gap: 10px; }
  }
`;

function normaliseQuestion(row) {
  // Safely parse options if Supabase returns it as a JSON string
  let options = row.options;
  if (typeof options === 'string') {
    try { options = JSON.parse(options); } catch { options = []; }
  }

  // Safely parse correct
  let correct = row.correct;
  if (typeof correct === 'string') {
    try {
      const parsed = JSON.parse(correct);
      correct = Array.isArray(parsed) ? parsed.map(Number) : Number(parsed);
    } catch {
      correct = Number(correct);
    }
  } else if (Array.isArray(correct)) {
    correct = correct.map(Number);
  } else {
    correct = Number(correct);
  }

  return {
    id: row.id,
    question: row.question,
    options,
    correct,
    explanation: row.explanation,
    topic: row.topic,
    multi: row.multi === true || row.multi === 'true',
  };
}

export default function Exam() {
  const navigate = useNavigate();
  const { slug } = useParams();

  // Data loading
  const [examName, setExamName]             = useState('');
  const [passPercentage, setPassPercentage] = useState(65);
  const [allQuestions, setAllQuestions]     = useState([]);
  const [loading, setLoading]               = useState(true);
  const [fetchError, setFetchError]         = useState(null);

  useEffect(() => {
    async function loadExamAndQuestions() {
      // 1. Fetch exam by slug to get its id, name and pass_percentage
      const { data: exam, error: examError } = await supabase
        .from('exams')
        .select('id, name, pass_percentage')
        .eq('slug', slug)
        .single();

      if (examError || !exam) {
        setFetchError(examError?.message || 'Exam not found');
        setLoading(false);
        return;
      }

      setExamName(exam.name);
      if (exam.pass_percentage != null) setPassPercentage(exam.pass_percentage);

      // 2. Fetch questions using the real exam id
      const { data, error } = await supabase
        .from('questions')
        .select('*')
        .eq('exam_id', exam.id)
        .order('question_number', { ascending: true });

      if (error) {
        setFetchError(error.message);
      } else {
        setAllQuestions(data.map(normaliseQuestion));
      }
      setLoading(false);
    }
    loadExamAndQuestions();
  }, [slug]);

  // Quiz state
  const [phase, setPhase]                     = useState('start');
  const [questions, setQuestions]             = useState([]);
  const [current, setCurrent]                 = useState(0);
  const [showNav, setShowNav]                 = useState(false);
  const [showHomeConfirm, setShowHomeConfirm] = useState(false);
  const [qState, setQState]                   = useState({ selected: [], submitted: false });
  const [resultsMap, setResultsMap]           = useState(new Map());

  const topicCount = new Set(allQuestions.map(q => q.topic)).size;

  const startQuiz = () => {
    setQuestions([...allQuestions]);
    setCurrent(0);
    setQState({ selected: [], submitted: false });
    setResultsMap(new Map());
    setShowNav(false);
    setShowHomeConfirm(false);
    setPhase('quiz');
  };

  const jumpToQuestion = (idx) => {
    const targetQ = questions[idx];
    const prev = resultsMap.get(targetQ?.id);
    setQState(prev ? { selected: prev.selected, submitted: true } : { selected: [], submitted: false });
    setCurrent(idx);
    setShowNav(false);
  };

  const q             = questions[current];
  const isMulti       = q?.multi === true;
  const score         = [...resultsMap.values()].filter(r => r.correct).length;
  const progress      = questions.length > 0 ? (resultsMap.size / questions.length) * 100 : 0;
  const correctArr    = q ? (Array.isArray(q.correct) ? q.correct : [q.correct]) : [];
  const currentResult = q ? resultsMap.get(q.id) : null;
  const submitted     = qState.submitted;
  const canSubmit     = qState.selected.length > 0;

  const passThreshold = passPercentage;
  const pct           = questions.length > 0 ? Math.round((score / questions.length) * 100) : 0;
  const passed        = pct >= passThreshold;
  const missed        = [...resultsMap.values()].filter(r => !r.correct);
  const circumference = 2 * Math.PI * 60;
  const strokeDash    = (pct / 100) * circumference;

  const getLetter = (idx) => ['A', 'B', 'C', 'D', 'E', 'F'][idx] || idx;

  const toggleOption = (idx) => {
    if (submitted) return;
    setQState(prev => {
      if (isMulti) {
        const already = prev.selected.includes(idx);
        return { ...prev, selected: already ? prev.selected.filter(i => i !== idx) : [...prev.selected, idx] };
      }
      return { ...prev, selected: [idx] };
    });
  };

  const submit = () => {
    if (!canSubmit || submitted) return;
    const isCorrect = isMulti
      ? qState.selected.length === correctArr.length && qState.selected.every(i => correctArr.includes(i))
      : correctArr.includes(qState.selected[0]);
    setResultsMap(prev => new Map(prev).set(q.id, { correct: isCorrect, selected: qState.selected, question: q.question, explanation: q.explanation }));
    setQState(prev => ({ ...prev, submitted: true }));
  };

  const next = () => {
    if (current + 1 >= questions.length) {
      setPhase('results');
    } else {
      const nextQ = questions[current + 1];
      const prev = resultsMap.get(nextQ?.id);
      setQState(prev ? { selected: prev.selected, submitted: true } : { selected: [], submitted: false });
      setCurrent(c => c + 1);
    }
  };

  const getOptionClass = (idx) => {
    if (!submitted) return `option-btn${qState.selected.includes(idx) ? ' selected' : ''}`;
    if (correctArr.includes(idx)) return 'option-btn correct-opt';
    if (qState.selected.includes(idx)) return 'option-btn wrong-opt';
    return 'option-btn';
  };

  const getNavBtnStyle = (idx) => {
    const targetQ = questions[idx];
    const result  = targetQ ? resultsMap.get(targetQ.id) : null;
    const isCurrent = idx === current;
    if (result?.correct)  return { background: 'rgba(34,197,94,0.15)',  borderColor: 'rgba(34,197,94,0.4)',  color: 'var(--success)', outline: isCurrent ? '2px solid var(--success)' : 'none' };
    if (result)           return { background: 'rgba(239,68,68,0.15)',  borderColor: 'rgba(239,68,68,0.4)',  color: 'var(--error)',   outline: isCurrent ? '2px solid var(--error)'   : 'none' };
    if (isCurrent)        return { background: 'rgba(0,212,255,0.1)',   borderColor: 'rgba(0,212,255,0.4)',  color: 'var(--accent)',  outline: '2px solid var(--accent)' };
    return {};
  };

  return (
    <>
      <style>{styles}</style>
      <div className="app">

        <header className="header">
          <div className="logo" onClick={() => navigate('/dashboard')}>
            <span className="logo-emoji">🐧</span>
            <div>
              <div className="logo-text">PassPenguin</div>
              <div className="logo-sub">{examName}</div>
            </div>
          </div>
          {phase === 'quiz' && (
            <div className="header-stats">
              <div className="stat-pill">
                <div className="stat-dot" style={{ background: 'var(--accent)' }} />
                {current + 1} / {questions.length}
              </div>
              <div className="stat-pill">
                <div className="stat-dot" style={{ background: 'var(--success)' }} />
                {score} correct
              </div>
              <button className="btn-nav-grid" onClick={() => setShowNav(v => !v)}>⊞ Questions</button>
              <button className="btn-home" onClick={() => setShowHomeConfirm(true)}>← Dashboard</button>
            </div>
          )}
        </header>

        <div className="progress-bar-container">
          <div className="progress-bar-fill" style={{ width: `${progress}%` }} />
        </div>

        <main className="main">

          {loading && (
            <div className="loading-screen">
              <div className="loading-spinner" />
              <p className="loading-text">Loading questions...</p>
            </div>
          )}

          {!loading && fetchError && (
            <div className="error-screen">
              <p className="error-title">Failed to load questions</p>
              <p className="error-msg">{fetchError}</p>
              <button className="btn-primary" onClick={() => window.location.reload()}>Try again</button>
            </div>
          )}

          {!loading && !fetchError && phase === 'start' && (
            <div className="start-screen">
              <p className="start-eyebrow">✦ Salesforce Certification Practice</p>
              <h1 className="start-title">{examName}</h1>
              <p className="start-sub">Test your knowledge with {allQuestions.length} curated questions covering all exam domains. Each question includes detailed explanations.</p>
              <div className="start-grid">
                <div className="start-card">
                  <span className="start-card-num">{allQuestions.length}</span>
                  <span className="start-card-label">Questions</span>
                </div>
                <div className="start-card">
                  <span className="start-card-num">{passPercentage}%</span>
                  <span className="start-card-label">Pass Score</span>
                </div>
                <div className="start-card">
                  <span className="start-card-num">{topicCount}</span>
                  <span className="start-card-label">Topics</span>
                </div>
              </div>
              <button className="btn-primary" onClick={startQuiz}>Begin Practice Exam →</button>
            </div>
          )}

          {phase === 'quiz' && q && (
            <div className="question-wrapper">
              <div className="question-meta">
                <span className="q-number">QUESTION {current + 1} OF {questions.length}</span>
                <span className="q-topic">{q.topic}</span>
              </div>
              <div className="question-card">
                {isMulti && <div className="multi-badge">◆ SELECT ALL THAT APPLY</div>}
                <div className="question-text">
                  {q.question.split(/(```[\s\S]*?```)/g).map((part, i) => {
                    if (part.startsWith('```') && part.endsWith('```')) {
                      const code = part.slice(3, -3).trim();
                      return <pre key={i} className="code-block"><code>{code}</code></pre>;
                    }
                    return <span key={i}>{part}</span>;
                  })}
                </div>
                <div className="options-grid">
                  {q.options.map((opt, idx) => (
                    <button key={idx} className={getOptionClass(idx)} onClick={() => toggleOption(idx)} disabled={submitted}>
                      <span className="option-letter">{getLetter(idx)}</span>
                      <span className="option-text">{opt}</span>
                    </button>
                  ))}
                </div>
                {submitted && currentResult && (
                  <div className={`feedback-box ${currentResult.correct ? 'correct-fb' : 'incorrect-fb'}`}>
                    <div className="feedback-header">
                      <span>{currentResult.correct ? '✓' : '✗'}</span>
                      <span style={{ color: currentResult.correct ? 'var(--success)' : 'var(--error)' }}>
                        {currentResult.correct ? 'Correct!' : 'Incorrect'}
                      </span>
                    </div>
                    <p className="feedback-text">{q.explanation}</p>
                  </div>
                )}
              </div>
              <div className="actions">
                {!submitted && <button className="btn-submit" onClick={submit} disabled={!canSubmit}>Check Answer</button>}
                {submitted && <button className="btn-next" onClick={next}>{current + 1 >= questions.length ? 'See Results' : 'Next Question →'}</button>}
              </div>
            </div>
          )}

          {phase === 'results' && (
            <div className="results-screen">
              <div className="results-hero">
                <div className="score-ring">
                  <svg width="160" height="160" viewBox="0 0 160 160">
                    <circle cx="80" cy="80" r="60" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="12" />
                    <circle cx="80" cy="80" r="60" fill="none" stroke={passed ? 'var(--success)' : 'var(--error)'} strokeWidth="12" strokeDasharray={`${strokeDash} ${circumference}`} strokeLinecap="round" />
                  </svg>
                  <div className="score-center">
                    <span className="score-pct">{pct}%</span>
                    <span className="score-label">{passed ? 'PASS' : 'FAIL'}</span>
                  </div>
                </div>
                <h2 className="results-title">{passed ? '🎉 Well Done!' : '📚 Keep Studying'}</h2>
                <p className="results-sub">{passed ? `You passed with ${pct}%. You're on track for the exam!` : `You scored ${pct}%. The passing threshold is ${passThreshold}%.`}</p>
              </div>
              <div className="results-breakdown">
                <div className="breakdown-card"><span className="breakdown-num" style={{ color: 'var(--success)' }}>{score}</span><span className="breakdown-label">CORRECT</span></div>
                <div className="breakdown-card"><span className="breakdown-num" style={{ color: 'var(--error)' }}>{resultsMap.size - score}</span><span className="breakdown-label">INCORRECT</span></div>
                <div className="breakdown-card"><span className="breakdown-num" style={{ color: 'var(--accent)' }}>{questions.length}</span><span className="breakdown-label">TOTAL</span></div>
              </div>
              <div className="results-actions">
                <button className="btn-primary" onClick={startQuiz}>Retake Exam</button>
                <button className="btn-secondary" onClick={() => navigate('/dashboard')}>Back to Dashboard</button>
              </div>
              {missed.length > 0 && (
                <div className="review-section">
                  <p className="review-title">⚑ Review Missed Questions ({missed.length})</p>
                  {missed.map((item, i) => (
                    <div className="review-item" key={item.id}>
                      <p className="review-q">{i + 1}. {item.question}</p>
                      <p className="review-exp">{item.explanation}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

        </main>

        {/* Question nav drawer */}
        {showNav && phase === 'quiz' && (
          <>
            <div className="nav-overlay" onClick={() => setShowNav(false)} />
            <div className="nav-drawer">
              <div className="nav-drawer-header">
                <span className="nav-drawer-title">Questions</span>
                <button className="nav-close" onClick={() => setShowNav(false)}>✕</button>
              </div>
              <div className="nav-legend">
                <span className="legend-item"><span className="legend-dot" style={{ background: 'rgba(34,197,94,0.4)' }} />Correct</span>
                <span className="legend-item"><span className="legend-dot" style={{ background: 'rgba(239,68,68,0.4)' }} />Incorrect</span>
                <span className="legend-item"><span className="legend-dot" style={{ background: 'rgba(0,212,255,0.4)' }} />Current</span>
                <span className="legend-item"><span className="legend-dot" style={{ background: 'var(--surface2)', border: '1px solid var(--border)' }} />Unanswered</span>
              </div>
              <div className="nav-grid">
                {questions.map((_, idx) => (
                  <button
                    key={idx}
                    className="nav-q-btn"
                    style={getNavBtnStyle(idx)}
                    onClick={() => jumpToQuestion(idx)}
                  >
                    {idx + 1}
                  </button>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Leave confirmation modal */}
        {showHomeConfirm && (
          <>
            <div className="nav-overlay" onClick={() => setShowHomeConfirm(false)} />
            <div className="confirm-modal">
              <p className="confirm-title">Leave this exam?</p>
              <p className="confirm-body">Your progress will be lost. Are you sure you want to go back to the dashboard?</p>
              <div className="confirm-actions">
                <button className="btn-secondary" onClick={() => setShowHomeConfirm(false)}>Stay</button>
                <button className="btn-danger" onClick={() => navigate('/dashboard')}>Leave</button>
              </div>
            </div>
          </>
        )}

      </div>
    </>
  );
}